import { ArrowRight, ChevronLeft, ChevronRight, Loader2, Pencil, Sparkles } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MediaPlayer } from "../components/MediaPlayer";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { useToast } from "../components/ui/use-toast";
import { useAuth } from "../contexts/AuthContext";
import { MediaItem } from "../models/Post";

interface SharedMediaItem extends MediaItem {
  url: string;
  thumbnailUrl: string | null;
  hasWatermark: boolean;
}

interface SharedPostData {
  id: string;
  shareId: string;
  description?: string;
  hashtags?: string[];
  mediaItems: SharedMediaItem[];
  mediaCount: number;
  ownerHasPaidPlan: boolean;
  overlayUrls?: Record<string, string>;
  templateId: string | null; // present only for showcase templates
}

export function SharePage() {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { toast } = useToast();

  const [postData, setPostData] = useState<SharedPostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [dimensions, setDimensions] = useState({ width: 400, height: 400 });
  const [cloning, setCloning] = useState(false);

  const currentMedia = postData?.mediaItems[currentIndex];

  // Calculate dimensions based on media aspect ratio and viewport
  const calculateDimensions = useCallback(() => {
    if (!currentMedia?.metadata?.dimensions) {
      setDimensions({ width: 400, height: 400 });
      return;
    }

    const mediaWidth = currentMedia.metadata.dimensions.width;
    const mediaHeight = currentMedia.metadata.dimensions.height;
    const aspectRatio = mediaWidth / mediaHeight;

    const isMobile = window.innerWidth < 768;

    let calculatedWidth: number;
    let calculatedHeight: number;

    if (isMobile) {
      calculatedWidth = window.innerWidth;
      calculatedHeight = calculatedWidth / aspectRatio;

      const maxHeight = window.innerHeight * 0.6;
      if (calculatedHeight > maxHeight) {
        calculatedHeight = maxHeight;
        calculatedWidth = calculatedHeight * aspectRatio;
      }
    } else {
      const maxWidth = window.innerWidth * 0.5;
      const maxHeight = window.innerHeight * 0.7;

      if (aspectRatio > maxWidth / maxHeight) {
        calculatedWidth = maxWidth;
        calculatedHeight = calculatedWidth / aspectRatio;
      } else {
        calculatedHeight = maxHeight;
        calculatedWidth = calculatedHeight * aspectRatio;
      }
    }

    setDimensions({
      width: Math.round(calculatedWidth),
      height: Math.round(calculatedHeight),
    });
  }, [currentMedia]);

  useEffect(() => {
    calculateDimensions();
    window.addEventListener("resize", calculateDimensions);
    return () => window.removeEventListener("resize", calculateDimensions);
  }, [calculateDimensions]);

  // Build mediaUrls map for MediaPlayer (includes overlay URLs)
  const mediaUrls: Record<string, string> = {};
  if (postData) {
    postData.mediaItems.forEach((item) => {
      if (item.url) {
        mediaUrls[item.path] = item.url;
      }
      if (item.thumbnailUrl && item.metadata.thumbnailPath) {
        mediaUrls[item.metadata.thumbnailPath] = item.thumbnailUrl;
      }
    });
    if (postData.overlayUrls) {
      Object.assign(mediaUrls, postData.overlayUrls);
    }
  }

  useEffect(() => {
    async function fetchSharedPost() {
      if (!shareId) return;

      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/api/share/${shareId}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            setError("This shared post is no longer available.");
          } else {
            setError("Failed to load shared post.");
          }
          return;
        }

        const data = await response.json();
        setPostData(data);
      } catch (err) {
        console.error("Error fetching shared post:", err);
        setError("Failed to load shared post.");
      } finally {
        setLoading(false);
      }
    }

    fetchSharedPost();
  }, [shareId]);

  const handlePrevious = () => {
    if (!postData) return;
    setCurrentIndex((prev) =>
      prev === 0 ? postData.mediaItems.length - 1 : prev - 1
    );
    setCurrentVideoTime(0);
  };

  const handleNext = () => {
    if (!postData) return;
    setCurrentIndex((prev) =>
      prev === postData.mediaItems.length - 1 ? 0 : prev + 1
    );
    setCurrentVideoTime(0);
  };

  const handleSignUp = () => {
    if (currentUser) {
      navigate("/created");
    } else {
      if (shareId) {
        sessionStorage.setItem("pendingShareView", shareId);
      }
      navigate("/login");
    }
  };

  const handleSkipTyping = () => {
    const templateId = postData?.templateId;
    const generatePath = templateId
      ? `/generate?templateId=${encodeURIComponent(templateId)}`
      : "/generate";

    if (currentUser) {
      navigate(generatePath);
      return;
    }
    // Stash the intended destination so the post-login flow can resume it.
    if (templateId) {
      sessionStorage.setItem("pendingSkipToGenerateTemplateId", templateId);
    } else {
      sessionStorage.removeItem("pendingSkipToGenerateTemplateId");
    }
    sessionStorage.setItem("pendingSkipToGenerate", "1");
    navigate(`/login?${new URLSearchParams(templateId ? { templateId } : {})}`);
  };

  const handleSaveAndEdit = async () => {
    if (!shareId) return;

    // If not logged in, store the shareId and redirect to login
    if (!currentUser) {
      sessionStorage.setItem("pendingShareClone", shareId);
      navigate(`/login?${new URLSearchParams({ shareClone: shareId, toolUrl: `${window.location.origin}/shared/${shareId}` })}`);
      return;
    }

    // Clone the post to user's account
    setCloning(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/share/${shareId}/clone`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${await currentUser.getIdToken()}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save post");
      }

      const { postId } = await response.json();

      toast({
        title: "Post saved!",
        description: "The post has been copied to your account.",
      });

      navigate(`/create-post?draftId=${encodeURIComponent(postId)}`);
    } catch (err: any) {
      console.error("Clone error:", err);
      toast({
        variant: "destructive",
        title: "Save failed",
        description: err.message || "Unable to save this post.",
      });
    } finally {
      setCloning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600" />
      </div>
    );
  }

  if (error || !postData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 p-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {error || "Post not found"}
        </h1>
        <p className="text-gray-600 mb-6">
          This post may have been deleted or sharing may have been disabled.
        </p>
        <Button onClick={() => window.open("https://wavegen.ai", "_blank")}>
          Go to WaveGen
        </Button>
      </div>
    );
  }

  const hasMultipleItems = postData.mediaItems.length > 1;

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <a
          href="https://wavegen.ai"
          className="flex items-center gap-2"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src="/icon.svg" alt="WaveGen Logo" className="h-6 w-auto" />
          <span className="text-foreground font-semibold">
            WaveGen<span className="text-brand">.ai</span>
          </span>
        </a>
        <Button variant="default" onClick={handleSignUp}>
          {currentUser ? "Go to Dashboard" : "Try WaveGen Free"}
        </Button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-0 md:p-8">
        <Card className="bg-white md:shadow-xl md:rounded-xl overflow-hidden flex flex-col md:flex-row max-w-full border-0 shadow-none rounded-none">
          {/* Media Section */}
          <div
            className="group bg-black flex items-center justify-center relative flex-shrink-0 md:rounded-l-xl overflow-hidden w-full md:w-auto"
            style={{
              width: window.innerWidth < 768 ? "100%" : `${dimensions.width}px`,
              height: `${dimensions.height}px`,
            }}
          >
            {currentMedia && (
              <MediaPlayer
                mediaItem={currentMedia}
                currentTime={currentVideoTime}
                onTimeUpdate={setCurrentVideoTime}
                isControlsVisible={true}
                autoPlay={false}
                isFreePlan={!postData.ownerHasPaidPlan}
                mediaUrls={mediaUrls}
                borderless
                key={`${currentMedia.path}-${currentIndex}`}
              />
            )}

            {/* Media counter */}
            {hasMultipleItems && (
              <div className="absolute top-4 left-4 bg-black/40 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm z-20">
                {currentIndex + 1} / {postData.mediaItems.length}
              </div>
            )}

            {/* Navigation arrows - hidden by default, shown on hover */}
            {hasMultipleItems && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-white/90 bg-black/40 hover:bg-black/60 backdrop-blur-sm z-20 rounded-full w-9 h-9 p-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  onClick={handlePrevious}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/90 bg-black/40 hover:bg-black/60 backdrop-blur-sm z-20 rounded-full w-9 h-9 p-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  onClick={handleNext}
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </>
            )}
          </div>

          {/* Right Panel */}
          <div
            className="w-full md:w-80 flex flex-col border-t md:border-t-0 md:border-l border-gray-100"
            style={{
              height:
                window.innerWidth >= 768 ? `${dimensions.height}px` : "auto",
            }}
          >
            {/* Scrollable Description */}
            <div className="flex-1 overflow-y-auto p-5 md:p-6">
              {postData.description || postData.hashtags?.length ? (
                <>
                  <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                    Description
                  </h3>
                  {postData.description && (
                    <p
                      className="notranslate text-gray-600 whitespace-pre-line mb-4 text-sm leading-relaxed"
                      translate="no"
                    >
                      {postData.description}
                    </p>
                  )}
                  {postData.hashtags && postData.hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {postData.hashtags.map((tag, i) => (
                        <span
                          key={i}
                          className="notranslate bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full text-xs font-medium"
                          translate="no"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-gray-400 text-sm italic">No description</p>
              )}
            </div>

            {/* Action Buttons (desktop only — mobile uses sticky bottom bar) */}
            <div className="hidden md:flex p-4 border-t border-gray-100 flex-col gap-2 flex-shrink-0 bg-gray-50/50">
              {/* Skip the typing — only on showcase templates */}
              {postData.templateId && (
                <button
                  type="button"
                  onClick={handleSkipTyping}
                  className="group w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-purple-100 bg-purple-50 hover:bg-purple-100 hover:border-purple-200 transition-colors text-left"
                >
                  <Sparkles className="h-4 w-4 text-purple-500 shrink-0" />
                  <span className="text-sm text-purple-700 flex-1 leading-snug">
                    <span className="font-semibold">Skip the typing</span>
                    {" "}— paste text or a URL and let AI generate your post
                  </span>
                  <ArrowRight className="h-4 w-4 text-purple-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </button>
              )}

              {/* Customize this template */}
              <Button
                variant="brand"
                onClick={handleSaveAndEdit}
                disabled={cloning}
                className="w-full"
              >
                {cloning ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Pencil className="h-4 w-4 mr-2" />
                    Customize this template
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      </main>

      {/* Mobile sticky CTA — shown only on small screens, fixed to viewport bottom */}
      <div className="md:hidden sticky bottom-0 inset-x-0 border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] flex flex-col gap-2 z-30">
        {postData.templateId && (
          <button
            type="button"
            onClick={handleSkipTyping}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-100 bg-purple-50 active:bg-purple-100 transition-colors text-left"
          >
            <Sparkles className="h-4 w-4 text-purple-500 shrink-0" />
            <span className="text-sm text-purple-700 flex-1 leading-snug">
              <span className="font-semibold">Skip the typing</span> — AI from text or URL
            </span>
            <ArrowRight className="h-4 w-4 text-purple-400 shrink-0" />
          </button>
        )}
        <Button
          variant="brand"
          onClick={handleSaveAndEdit}
          disabled={cloning}
          className="w-full"
        >
          {cloning ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Pencil className="h-4 w-4 mr-2" />
              Customize this template
            </>
          )}
        </Button>
      </div>

      {/* Footer */}
      <footer className="py-4 text-center text-gray-400 text-sm flex-shrink-0">
        Made with{" "}
        <a
          href="https://wavegen.ai"
          className="text-orange-500 hover:text-orange-600 transition-colors"
          target="_blank"
          rel="noopener noreferrer"
        >
          wavegen.ai
        </a>
      </footer>
    </div>
  );
}
